import * as React from 'react';
import { useState, useEffect } from 'react';
import { colors, shadows } from '../../employees/styles/personalakteStyles';

export interface DateRange {
  start: Date;
  end: Date;
}

export type DateRangeMode = 'bisHeute' | 'ganzerMonat' | 'last7Days' | 'last30Days' | 'thisMonth' | 'lastMonth' | 'custom';

interface DateRangeFilterProps {
  onRangeChange: (range: DateRange) => void;
  defaultMode?: DateRangeMode;
  initialMonth?: Date;
  initialRange?: { start: string; end: string };
}

export default function DateRangeFilter({
  onRangeChange,
  defaultMode = 'last7Days',
  initialMonth,
  initialRange,
}: DateRangeFilterProps): React.JSX.Element {
  const hasInitialRange = !!(initialRange?.start && initialRange?.end);
  const [mode, setMode] = useState<DateRangeMode>(hasInitialRange ? 'custom' : 'thisMonth');
  const [customStart, setCustomStart] = useState<string>(initialRange?.start ?? '');
  const [customEnd, setCustomEnd] = useState<string>(initialRange?.end ?? '');
  const [selectedMonth, setSelectedMonth] = useState<Date>(() => {
    if (initialRange?.start) {
      const d = new Date(initialRange.start);
      return new Date(d.getFullYear(), d.getMonth(), 1);
    }
    return initialMonth ?? new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  });

  useEffect(() => {
    if (hasInitialRange) {
      const range = computeRange('custom', initialRange!.start, initialRange!.end);
      onRangeChange(range);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const computeRange = (selectedMode: DateRangeMode, customStartDate?: string, customEndDate?: string): DateRange => {
    const now = new Date();
    let start: Date;
    let end: Date;

    switch (selectedMode) {
      case 'last7Days':
        start = new Date(now);
        start.setDate(now.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;

      case 'last30Days':
        start = new Date(now);
        start.setDate(now.getDate() - 30);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;

      case 'thisMonth':
        start = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
        end = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0, 23, 59, 59, 999);
        break;

      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        break;

      case 'custom':
        if (customStartDate) {
          start = new Date(`${customStartDate}T00:00:00`);
        } else {
          start = new Date(now);
          start.setDate(now.getDate() - 7);
          start.setHours(0, 0, 0, 0);
        }
        if (customEndDate) {
          end = new Date(`${customEndDate}T23:59:59`);
        } else {
          end = new Date(now);
          end.setHours(23, 59, 59, 999);
        }
        break;

      default:
        start = new Date(now);
        start.setDate(now.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
    }

    return { start, end };
  };

  useEffect(() => {
    if (mode === 'custom' || mode === 'bisHeute' || mode === 'ganzerMonat') return;
    const range = computeRange(mode, customStart, customEnd);
    onRangeChange(range);
  }, [mode, selectedMonth, customStart, customEnd]);

  const pillStyle: React.CSSProperties = {
    padding: '6px 12px',
    border: 'none',
    background: 'rgba(255, 255, 255, 0.9)',
    color: colors.textMuted,
    borderRadius: '14px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: 600,
    transition: 'all 0.2s ease',
    boxShadow: shadows.soft,
  };

  const pillActiveStyle: React.CSSProperties = {
    ...pillStyle,
    background: `linear-gradient(145deg, ${colors.slate} 0%, ${colors.navy} 100%)`,
    color: '#fff',
    boxShadow: `0 4px 12px rgba(14, 19, 51, 0.3), 0 0 8px rgba(82, 113, 165, 0.2), inset 0 1px 0 rgba(255,255,255,0.2)`,
    textShadow: '0 1px 2px rgba(0,0,0,0.15)',
  };

  const navBtnStyle: React.CSSProperties = {
    width: '26px',
    height: '26px',
    borderRadius: '8px',
    border: 'none',
    background: 'rgba(255, 255, 255, 0.9)',
    color: colors.textMuted,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    transition: 'all 0.15s ease',
    boxShadow: shadows.soft,
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      flexWrap: 'wrap',
    }}>
      {/* Month Navigation - Glass Container */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRadius: '16px',
        padding: '4px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        border: '1px solid rgba(255, 255, 255, 0.8)',
      }}>
        <button
          aria-label="Prev Month"
          title="Prev Month"
          style={navBtnStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = `rgba(82, 113, 165, 0.1)`;
            e.currentTarget.style.color = colors.slate;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)'; // reset
            e.currentTarget.style.color = colors.textMuted;
          }}
          onClick={() => {
            const y = selectedMonth.getFullYear();
            const m = selectedMonth.getMonth();
            setSelectedMonth(new Date(y, m - 1, 1));
            setMode('thisMonth');
          }}
        >
          ◀
        </button>
        <span style={{
          minWidth: 105,
          textAlign: 'center',
          fontWeight: 600,
          color: colors.textBody,
          padding: '4px 10px',
          fontSize: '12px',
        }}>
          {selectedMonth.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
        </span>
        <button
          aria-label="Next Month"
          title="Next Month"
          style={navBtnStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = `rgba(82, 113, 165, 0.1)`;
            e.currentTarget.style.color = colors.slate;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)'; // reset
            e.currentTarget.style.color = colors.textMuted;
          }}
          onClick={() => {
            const y = selectedMonth.getFullYear();
            const m = selectedMonth.getMonth();
            setSelectedMonth(new Date(y, m + 1, 1));
            setMode('thisMonth');
          }}
        >
          ▶
        </button>
      </div>

      {/* Presets - Glass Container */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRadius: '18px',
        padding: '4px',
        gap: '2px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        border: '1px solid rgba(255, 255, 255, 0.8)',
      }}>
        <button
          style={mode === 'bisHeute' ? pillActiveStyle : pillStyle}
          onMouseEnter={(e) => {
            if (mode !== 'bisHeute') {
              e.currentTarget.style.background = `rgba(82, 113, 165, 0.08)`;
              e.currentTarget.style.color = colors.slate;
            }
          }}
          onMouseLeave={(e) => {
            if (mode !== 'bisHeute') {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)'; // reset
              e.currentTarget.style.color = colors.textMuted;
            }
          }}
          onClick={() => {
            if (mode === 'bisHeute') {
              setMode('thisMonth');
            } else {
              setMode('bisHeute');
              const first = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
              const now = new Date();
              const sameMonth = now.getFullYear() === selectedMonth.getFullYear() && now.getMonth() === selectedMonth.getMonth();
              const end = sameMonth ? new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999) : new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0, 23, 59, 59, 999);
              onRangeChange({ start: first, end });
            }
          }}
        >
          Bis heute
        </button>
        <button
          style={mode === 'ganzerMonat' ? pillActiveStyle : pillStyle}
          onMouseEnter={(e) => {
            if (mode !== 'ganzerMonat') {
              e.currentTarget.style.background = `rgba(82, 113, 165, 0.08)`;
              e.currentTarget.style.color = colors.slate;
            }
          }}
          onMouseLeave={(e) => {
            if (mode !== 'ganzerMonat') {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)'; // reset
              e.currentTarget.style.color = colors.textMuted;
            }
          }}
          onClick={() => {
            if (mode === 'ganzerMonat') {
              setMode('thisMonth');
            } else {
              setMode('ganzerMonat');
              const first = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
              const last  = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0, 23, 59, 59, 999);
              onRangeChange({ start: first, end: last });
            }
          }}
        >
          Ganzer Monat
        </button>
        <button
          style={mode === 'last7Days' ? pillActiveStyle : pillStyle}
          onMouseEnter={(e) => {
            if (mode !== 'last7Days') {
              e.currentTarget.style.background = `rgba(82, 113, 165, 0.08)`;
              e.currentTarget.style.color = colors.slate;
            }
          }}
          onMouseLeave={(e) => {
            if (mode !== 'last7Days') {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)'; // reset
              e.currentTarget.style.color = colors.textMuted;
            }
          }}
          onClick={() => setMode(mode === 'last7Days' ? 'thisMonth' : 'last7Days')}
        >
          Last 7 Days
        </button>
        <button
          style={mode === 'last30Days' ? pillActiveStyle : pillStyle}
          onMouseEnter={(e) => {
            if (mode !== 'last30Days') {
              e.currentTarget.style.background = `rgba(82, 113, 165, 0.08)`;
              e.currentTarget.style.color = colors.slate;
            }
          }}
          onMouseLeave={(e) => {
            if (mode !== 'last30Days') {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)'; // reset
              e.currentTarget.style.color = colors.textMuted;
            }
          }}
          onClick={() => setMode(mode === 'last30Days' ? 'thisMonth' : 'last30Days')}
        >
          Last 30 Days
        </button>
        <button
          style={mode === 'custom' ? pillActiveStyle : pillStyle}
          onMouseEnter={(e) => {
            if (mode !== 'custom') {
              e.currentTarget.style.background = `rgba(82, 113, 165, 0.08)`;
              e.currentTarget.style.color = colors.slate;
            }
          }}
          onMouseLeave={(e) => {
            if (mode !== 'custom') {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)'; // reset
              e.currentTarget.style.color = colors.textMuted;
            }
          }}
          onClick={() => setMode(mode === 'custom' ? 'thisMonth' : 'custom')}
        >
          Custom
        </button>
      </div>

      {/* Custom Range Inputs */}
      {mode === 'custom' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderRadius: '14px',
          padding: '4px 8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
          border: '1px solid rgba(255, 255, 255, 0.8)',
        }}>
          <input
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              padding: '4px 8px',
              fontSize: '11px',
              background: 'rgba(255, 255, 255, 0.9)',
              color: colors.textBody,
              outline: 'none',
            }}
          />
          <span style={{ fontSize: '11px', color: colors.textLight }}>–</span>
          <input
            type="date"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              padding: '4px 8px',
              fontSize: '11px',
              background: 'rgba(255, 255, 255, 0.9)',
              color: colors.textBody,
              outline: 'none',
            }}
          />
          <button
            style={{
              padding: '5px 12px',
              border: 'none',
              background: `linear-gradient(135deg, ${colors.slate} 0%, ${colors.navy} 100%)`,
              color: '#fff',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 600,
              boxShadow: `0 2px 6px rgba(14, 19, 51, 0.25)`,
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = `0 4px 10px rgba(14, 19, 51, 0.35)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `0 2px 6px rgba(14, 19, 51, 0.25)`;
            }}
            onClick={() => {
              const range = computeRange('custom', customStart, customEnd);
              onRangeChange(range);
            }}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
}
